function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Constants } from '@shared/utils/constants';
import { createSetCurrentViewAction } from '@shared/actions/actions';
import { loadTileData } from '@shared/actions/tileDataThunkActions';
import { useLocationChange } from '@shared/hooks/useLocationChange';
import { useClientRoutes } from '@shared/hooks/useClientRoutes';
import { PATHS_TO_VIEW_MAP } from './pathsViewMap';
import AppView from '@shared/containers/AppView';
const Home = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/home'));
const GalleryPage = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/galleryPage'));
const AppDetails = /*#__PURE__*/ React.lazy(()=>import('./containers/appDetails'));
const ServiceDetails = /*#__PURE__*/ React.lazy(()=>import('./containers/serviceDetails'));
const AuthRedirect = /*#__PURE__*/ React.lazy(()=>import('@shared/components/AuthRedirect'));
const Checkout = /*#__PURE__*/ React.lazy(()=>import('./containers/LegacyCheckoutHydration'));
const SitemapContainer = /*#__PURE__*/ React.lazy(()=>import('./containers/sitemap'));
const PartnerContainer = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/partners'));
const PartnersHostContainer = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/partnersHost'));
const MyReviews = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/myReview'));
const DynamicCampaign = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/dynamicCampaign'));
const UserFavouriteDetail = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/userFavouriteDetail'));
const UserPrivateOffersGallery = /*#__PURE__*/ React.lazy(()=>import('@shared/containers/userPrivateOffersGallery'));
/**
 * Where actual routes should be declared, because the object is also used in the SSR to identify matched routes
 */ export const getRoutes = ()=>[
        {
            path: ':locale',
            element: /*#__PURE__*/ _jsx(AppView, {}),
            children: [
                {
                    index: true,
                    element: /*#__PURE__*/ _jsx(Home, {})
                },
                {
                    path: Constants.home,
                    element: /*#__PURE__*/ _jsx(Home, {})
                },
                {
                    path: 'marketplace',
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.appsGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: Constants.cloudsIndustryGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: 'product/:productId/:entityId',
                    element: /*#__PURE__*/ _jsx(AppDetails, {})
                },
                {
                    path: 'marketplace/checkout/:entityId',
                    element: /*#__PURE__*/ _jsx(Checkout, {})
                },
                {
                    path: 'partners',
                    element: /*#__PURE__*/ _jsx(PartnerContainer, {})
                },
                {
                    path: 'user/my-reviews',
                    element: /*#__PURE__*/ _jsx(MyReviews, {})
                },
                {
                    path: Constants.csGallery,
                    element: /*#__PURE__*/ _jsx(GalleryPage, {})
                },
                {
                    path: `${Constants.csGallery}/:entityId`,
                    element: /*#__PURE__*/ _jsx(ServiceDetails, {})
                },
                {
                    path: `${Constants.partnersAppSource}/*`,
                    element: /*#__PURE__*/ _jsx(PartnersHostContainer, {})
                },
                {
                    path: `marketplace/consultingservices/:entityId`,
                    element: /*#__PURE__*/ _jsx(ServiceDetails, {})
                },
                {
                    path: `campaigns/:publisher/:id`,
                    element: /*#__PURE__*/ _jsx(DynamicCampaign, {})
                },
                {
                    path: `user/my-favourites`,
                    element: /*#__PURE__*/ _jsx(UserFavouriteDetail, {})
                },
                {
                    path: `user/private-offers`,
                    element: /*#__PURE__*/ _jsx(UserPrivateOffersGallery, {})
                },
                {
                    path: ':privateOffer/product/:productId/:entityId',
                    element: /*#__PURE__*/ _jsx(AppDetails, {})
                },
                {
                    path: 'sitemap',
                    element: /*#__PURE__*/ _jsx(SitemapContainer, {})
                }
            ]
        },
        {
            path: 'redirect',
            element: /*#__PURE__*/ _jsx(AuthRedirect, {})
        }
    ];
export const getView = (routeMatch)=>routeMatch.route.index ? PATHS_TO_VIEW_MAP.home : PATHS_TO_VIEW_MAP[routeMatch.route.path];
const Router = ()=>{
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const setCurrentView = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function*(view, featureFlag = '') {
            if (!view) {
                return;
            }
            const query = new URLSearchParams(location.search);
            if (featureFlag && query.get(featureFlag) === 'true') {
                // the route is under a feature flag but the query param didn't have the feature flag set
                // treat is as a route never existed
                navigate('/404');
            }
            dispatch(createSetCurrentViewAction({
                currentView: view
            }));
            // @ts-expect-error known dispatch type error
            dispatch(loadTileData());
        });
        return function(view) {
            return _ref.apply(this, arguments);
        };
    }(), [
        dispatch,
        location,
        navigate
    ]);
    const clientRoutes = useMemo(()=>getRoutes(), []);
    const routes = useClientRoutes({
        routes: clientRoutes
    });
    useLocationChange({
        setCurrentView,
        map: PATHS_TO_VIEW_MAP,
        routes
    });
    return useRoutes(routes);
};
export default Router;
